import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  ReactNode,
  createContext,
  useState,
  useMemo,
  useRef,
} from 'react'
import { Motif, MotifResponse } from '../types/motifs'
import { useAxiosStrapi } from '../utils/axios'
import {
  filterByTag,
  filterByRegion,
  filterByKeyword,
  mapMotifResponse,
  filterByBlacklist,
  filterByWhitelist,
} from '../utils/motifs'
import { useStorage } from '../utils/localStorage'
import { useEnv } from '../utils/useEnv'
import { useContextPanels } from './panelsContext'
import { useContextAppStatus } from './appStatusContext'
import { loadThemesFromBackend, loadTagsFromBackend, Tag } from '../constants/tags'
import { Region } from '../constants/geographicFilters'
import { generateMap, MotifMap } from '../components/MotifMap/generate'
import { ReactZoomPanPinchState } from 'react-zoom-pan-pinch'
import { UIElement } from '../types/UIElements'

interface ContextType {
  allMotifs: Motif[]
  sortedMotifs: Motif[]
  filteredMotifs: Motif[]
  areDetailsOpen: boolean
  setDetailsOpen: Dispatch<SetStateAction<boolean>>
  setTargetedMotif: Dispatch<SetStateAction<number | undefined>>
  tagFilter: Tag | undefined
  applyTagFilter: (tag: Tag | undefined) => void
  keywordFilter: number | undefined
  applyKeywordFilter: (keywordId: number | undefined) => void
  geoFilter: Region | undefined
  applyGeoFilter: (region: Region | undefined) => void
  motifDetailsFadeOut: boolean
  setMotifDetailsFadeOut: Dispatch<SetStateAction<boolean>>
  motifMap: MotifMap | undefined
  motifMapViewport: React.MutableRefObject<ReactZoomPanPinchState | undefined>
  geographicMapViewport: React.MutableRefObject<GeographicMapViewport | undefined>
  motifImageCache: React.MutableRefObject<Map<number, MotifImageCache | null>>
}

export interface GeographicMapViewport {
  center: [number, number]
  zoom: number
}

interface Props {
  children: ReactNode
}

interface MotifImageCache {
  blob: Blob
  url: string
  blobXs: Blob
  urlXs: string
}

const preloadImage = (url: string, preloadSet: Set<string>) => {
  if (!url || preloadSet.has(url)) return

  preloadSet.add(url)

  // Hint the browser to fetch this image with priority when opening details.
  if (typeof document !== 'undefined') {
    const preloadLink = document.createElement('link')
    preloadLink.rel = 'preload'
    preloadLink.as = 'image'
    preloadLink.href = url
    document.head.appendChild(preloadLink)
  }

  const img = new Image()
  img.src = url
}

const preloadMotifDetailImages = (motif: Motif, preloadSet: Set<string>) => {
  const detailImageUrls = [
    motif.vectorizedUrl,
    motif.originalSVGUrl,
    motif.photoUrl,
    ...(motif.AdditionalPhotos ?? []),
  ].filter(Boolean) as string[]

  detailImageUrls.forEach(url => preloadImage(url, preloadSet))
}

const Context = createContext({} as ContextType)

const MotifDetailsContext = ({ children }: Props) => {
  const { store, getStored } = useStorage()
  const { isLocalDev } = useEnv()
  const { get } = useAxiosStrapi()
  const { isPanelOpen, closeAll, closePanel } = useContextPanels()
  const { setIsInitialLoading, showErrorMessage } = useContextAppStatus()
  const [allMotifs, setAllMotifs] = useState<Motif[]>([])
  const [sortedMotifs, setSortedMotifs] = useState<Motif[]>([])
  const [areDetailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [targetedMotif, setTargetedMotif] = useState<number>()
  const [tagFilter, setTagFilter] = useState<Tag | undefined>()
  const [keywordFilter, setKeywordFilter] = useState<number | undefined>()
  const [geoFilter, setGeoFilter] = useState<Region | undefined>()
  const [filteredMotifs, setFilteredMotifs] = useState<Motif[]>([])
  const [motifDetailsFadeOut, setMotifDetailsFadeOut] = useState<boolean>(false)
  const motifMap = useMemo(() => {
    if (allMotifs.length === 0) return

    return generateMap(allMotifs, window.innerWidth, window.innerHeight)
  }, [allMotifs])
  const motifMapViewport = useRef<ReactZoomPanPinchState | undefined>(undefined)
  const geographicMapViewport = useRef<GeographicMapViewport | undefined>(undefined)
  const motifImageCache = useRef(new Map<number, MotifImageCache | null>()) // null means loading
  const detailImagePreloadSet = useRef(new Set<string>())

  const fetchMotifs = async () => {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL
      const infoResponse = await fetch(`${backendUrl}/info`)
      if (!infoResponse.ok) {
        throw new Error(`HTTP error! Status: ${infoResponse.status}`)
      }
      const infoData = await infoResponse.json()
      console.log('Info Data:', infoData)
    } catch (error) {
      console.error('Error fetching /info:', error)
    }

    try {
      setIsInitialLoading(true)
      const allData: MotifResponse[] = []
      const pageSize = 200
      let currentPage = 0
      let hasMoreData = true

      while (hasMoreData) {
        const start = currentPage * pageSize
        const response = await get(`/cards?_start=${start}&_limit=${pageSize}`)
        const pageData = response.data

        if (pageData.length === 0) {
          hasMoreData = false
        } else {
          allData.push(...pageData)
          // If we got less than the page size, we've reached the end
          if (pageData.length < pageSize) {
            hasMoreData = false
          } else {
            currentPage++
          }
        }
      }

      const motifs = mapMotifResponse(allData)

      // we initalize the preFilteredMotifs variable with all motifs, and then we apply
      // either the blacklist or whitelist filter if they are set in the .env-cmdrc.json file.
      let preFilteredMotifs = motifs

      if (
        import.meta.env.VITE_BLACKLIST_FILTER_TAGS &&
        import.meta.env.VITE_WHITELIST_FILTER_TAGS
      ) {
        console.error(
          'You can only use either VITE_BLACKLIST_FILTER_TAGS or VITE_WHITELIST_FILTER_TAGS, not both at the same time. Falling back to no pre-filtering.',
        )
      }

      if (import.meta.env.VITE_BLACKLIST_FILTER_TAGS) {
        preFilteredMotifs = filterByBlacklist(motifs)
      }

      if (import.meta.env.VITE_WHITELIST_FILTER_TAGS) {
        preFilteredMotifs = filterByWhitelist(motifs)
      }

      // we save all the motifs, so pre-filtering only affects what is shown initially
      if (isLocalDev) {
        store<Motif[]>('all-motifs', motifs)
      }
      setAllMotifs(preFilteredMotifs)
    } catch (e) {
      showErrorMessage('APIErrorMessage')
      console.log('Could not fetch motifs', e)
    } finally {
      setIsInitialLoading(false)
    }
  }

  useEffect(() => {
    const initializeMotifs = async () => {
      // Keep tag-id map in sync with backend before motif filters are applied.
      await loadThemesFromBackend()
      await loadTagsFromBackend()

      const storedMotifs = getStored<Motif[]>('all-motifs')
      // Getting all the motifs is a crazy long request. To speed things up I implemented the use of local storage
      // so if LOCAL_DEV is set to 'true' we only need to fetch the data once and not every single page reload.
      // Comment this if block out if you need to re-fetch the motifs.
      if (isLocalDev && storedMotifs?.length) {
        setAllMotifs(storedMotifs)
        return
      }

      await fetchMotifs()
    }

    void initializeMotifs()
  }, [])

  useEffect(() => {
    if (!targetedMotif || isNaN(Number(targetedMotif))) return

    const motifSource = tagFilter || geoFilter || keywordFilter ? filteredMotifs : allMotifs

    // If we have a filter, we want to use the 'filteredMotifs' array as the base
    const motifIndex = motifSource.findIndex(m => m.id === Number(targetedMotif))

    if (motifIndex < 0) {
      setTargetedMotif(undefined)
      return
    }

    const targeted = motifSource[motifIndex]
    if (targeted) {
      preloadMotifDetailImages(targeted, detailImagePreloadSet.current)
    }

    const sorted =
      tagFilter || geoFilter || keywordFilter
        ? [
            ...filteredMotifs.slice(motifIndex, filteredMotifs.length),
            ...filteredMotifs.slice(0, motifIndex),
          ]
        : [...allMotifs.slice(motifIndex, allMotifs.length), ...allMotifs.slice(0, motifIndex)]

    setSortedMotifs(sorted)
    if (isPanelOpen(UIElement.GALLERY)) {
      closePanel(UIElement.GALLERY)
    }
    if (isPanelOpen(UIElement.ABOUT)) {
      closePanel(UIElement.ABOUT)
    }
    setDetailsOpen(true)

    if (!isPanelOpen(UIElement.CONTROLS) && !isPanelOpen(UIElement.GEOFILTER)) {
      closeAll()
    }

    setTargetedMotif(undefined)
  }, [targetedMotif])

  useEffect(() => {
    if (!tagFilter && !geoFilter && keywordFilter === undefined) {
      setFilteredMotifs([...allMotifs])
    } else if (tagFilter) {
      const filteredMotifs = filterByTag(allMotifs, tagFilter)
      setFilteredMotifs(filteredMotifs)
    } else if (geoFilter) {
      const filteredMotifs = filterByRegion(allMotifs, geoFilter)
      setFilteredMotifs(filteredMotifs)
    } else if (keywordFilter !== undefined) {
      const filteredMotifs = filterByKeyword(allMotifs, keywordFilter)
      setFilteredMotifs(filteredMotifs)
    }
  }, [tagFilter, geoFilter, keywordFilter, allMotifs])

  const applyTagFilter = (tag: Tag | undefined) => {
    setGeoFilter(undefined)
    setKeywordFilter(undefined)
    setTagFilter(tag)
  }

  const applyGeoFilter = (region: Region | undefined) => {
    setTagFilter(undefined)
    setKeywordFilter(undefined)
    setGeoFilter(region)
  }

  const applyKeywordFilter = (keywordId: number | undefined) => {
    setTagFilter(undefined)
    setGeoFilter(undefined)
    setKeywordFilter(keywordId)
  }

  return (
    <Context.Provider
      value={{
        allMotifs,
        sortedMotifs,
        filteredMotifs,
        areDetailsOpen,
        setDetailsOpen,
        setTargetedMotif,
        tagFilter,
        keywordFilter,
        geoFilter,
        applyTagFilter,
        applyKeywordFilter,
        applyGeoFilter,
        motifDetailsFadeOut,
        setMotifDetailsFadeOut,
        motifMap,
        motifMapViewport,
        geographicMapViewport,
        motifImageCache,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useContextMotifDetails = () => {
  return useContext(Context)
}

export default MotifDetailsContext
